import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';

const TROPHY_DESIGNS = [
  { id: 'liberty', name: 'Liberty (Bible & Candle)', icon: '🗽' },
  { id: 'sword', name: 'Sword', icon: '⚔️' },
  { id: 'shield', name: 'Shield', icon: '🛡️' },
  { id: 'cup', name: 'Trophy Cup', icon: '🏆' },
  { id: 'heart', name: 'Heart', icon: '❤️' },
  { id: 'carrot_basket', name: 'Carrot Basket', icon: '🥕' }
];

// Default nameplate text for each trophy type
const DEFAULT_NAMEPLATE_TEXT = {
  'completion': 'Completion',
  'quiz_out': 'Quiz Out',
  'round_win': 'Round Winner',
  'bowl_win': 'Bowl Champion'
};

const TrophySelector = ({ 
  trophyType, 
  trophyConfig, 
  onUpdate, 
  title, 
  description,
  allowedColors = ['gold', 'silver', 'bronze'] 
}) => {
  // Get default color based on allowed colors
  const getDefaultColor = () => {
    if (trophyConfig?.color) return trophyConfig.color;
    if (allowedColors.includes('bronze')) return 'bronze';
    if (allowedColors.includes('silver')) return 'silver';
    return 'gold';
  };

  // Get default nameplate text based on trophy type if not provided
  const getDefaultNameplateText = () => {
    if (trophyConfig?.nameplate_text) return trophyConfig.nameplate_text;
    return DEFAULT_NAMEPLATE_TEXT[trophyType] || '';
  };

  const [selectedDesign, setSelectedDesign] = useState(trophyConfig?.design || 'liberty');
  const [selectedColor, setSelectedColor] = useState(getDefaultColor());
  const [nameplateText, setNameplateText] = useState(getDefaultNameplateText());
  const [fontSize, setFontSize] = useState(trophyConfig?.font_size || 0.75);
  
  // Track if we've sent initial update for auto-filled text
  const hasInitialized = useRef(false);

  // Send initial update to parent if nameplate was auto-filled
  useEffect(() => {
    if (!hasInitialized.current && !trophyConfig?.nameplate_text && DEFAULT_NAMEPLATE_TEXT[trophyType]) {
      hasInitialized.current = true;
      // Use setTimeout to avoid setState during render
      setTimeout(() => {
        onUpdate({
          design: selectedDesign,
          color: selectedColor,
          nameplate_text: nameplateText,
          font_size: fontSize
        });
      }, 0);
    }
  }, []); // Empty deps - only run once on mount

  const handleDesignChange = (design) => {
    setSelectedDesign(design);
    
    // Special handling for carrot_basket - only has gold and silver
    let newColor = selectedColor;
    if (design === 'carrot_basket' && selectedColor === 'bronze') {
      // If carrot basket is selected and current color is bronze, default to gold
      newColor = 'gold';
      setSelectedColor(newColor);
    }
    
    onUpdate({
      design,
      color: newColor,
      nameplate_text: nameplateText,
      font_size: fontSize
    });
  };

  const handleColorChange = (color) => {
    setSelectedColor(color);
    onUpdate({
      design: selectedDesign,
      color,
      nameplate_text: nameplateText,
      font_size: fontSize
    });
  };

  const handleTextChange = (text) => {
    setNameplateText(text);
    onUpdate({
      design: selectedDesign,
      color: selectedColor,
      nameplate_text: text,
      font_size: fontSize
    });
  };

  const handleFontSizeChange = (size) => {
    setFontSize(size);
    onUpdate({
      design: selectedDesign,
      color: selectedColor,
      nameplate_text: nameplateText,
      font_size: size
    });
  };

  const getTrophyImagePath = () => {
    return `/trophies/${selectedDesign}_${selectedColor}.png`;
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left side: Trophy selection */}
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Select Trophy Design</Label>
              <div className="grid grid-cols-2 gap-2">
                {TROPHY_DESIGNS.map((trophy) => (
                  <button
                    key={trophy.id}
                    onClick={() => handleDesignChange(trophy.id)}
                    className={`p-3 border rounded-lg text-center transition-all ${
                      selectedDesign === trophy.id
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{trophy.icon}</div>
                    <div className="text-xs font-medium">{trophy.name}</div>
                  </button>
                ))}
              </div>
              {selectedDesign === 'carrot_basket' && (
                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                  <strong>Note:</strong> Carrot Basket only available in Gold & Silver
                </div>
              )}
            </div>

            <div>
              <Label className="mb-2 block">Trophy Color</Label>
              <div className="flex gap-2">
                {allowedColors.includes('gold') && (
                  <button
                    onClick={() => handleColorChange('gold')}
                    className={`flex-1 p-3 border rounded-lg transition-all ${
                      selectedColor === 'gold'
                        ? 'border-yellow-500 bg-yellow-50 shadow-md'
                        : 'border-gray-300 hover:border-yellow-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">🥇</div>
                    <div className="text-xs font-medium">Gold</div>
                  </button>
                )}
                {allowedColors.includes('silver') && (
                  <button
                    onClick={() => handleColorChange('silver')}
                    className={`flex-1 p-3 border rounded-lg transition-all ${
                      selectedColor === 'silver'
                        ? 'border-gray-400 bg-gray-50 shadow-md'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-2xl mb-1">🥈</div>
                    <div className="text-xs font-medium">Silver</div>
                  </button>
                )}
                {allowedColors.includes('bronze') && (
                  <button
                    onClick={() => handleColorChange('bronze')}
                    className={`flex-1 p-3 border rounded-lg transition-all ${
                      selectedColor === 'bronze'
                        ? 'border-orange-600 bg-orange-50 shadow-md'
                        : 'border-gray-300 hover:border-orange-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">🥉</div>
                    <div className="text-xs font-medium">Bronze</div>
                  </button>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor={`nameplate-${trophyType}`} className="mb-2 block">
                Nameplate Text
              </Label>
              <Input
                id={`nameplate-${trophyType}`}
                value={nameplateText}
                onChange={(e) => handleTextChange(e.target.value)}
                placeholder="Enter trophy nameplate text"
                maxLength={50}
              />
              <p className="text-xs text-gray-500 mt-1">
                {nameplateText.length}/50 characters
              </p>
            </div>

            <div>
              <Label htmlFor={`fontsize-${trophyType}`} className="mb-2 block">
                Font Size: {fontSize.toFixed(2)}rem
              </Label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleFontSizeChange(Math.max(0.4, fontSize - 0.05))}
                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded font-bold"
                >
                  −
                </button>
                <input
                  type="range"
                  id={`fontsize-${trophyType}`}
                  min="0.4"
                  max="1.2"
                  step="0.05"
                  value={fontSize}
                  onChange={(e) => handleFontSizeChange(parseFloat(e.target.value))}
                  className="flex-1"
                />
                <button
                  onClick={() => handleFontSizeChange(Math.min(1.2, fontSize + 0.05))}
                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded font-bold"
                >
                  +
                </button>
                <button
                  onClick={() => handleFontSizeChange(0.75)}
                  className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                >
                  Reset
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Adjust to ensure text fits comfortably in nameplate
              </p>
            </div>
          </div>

          {/* Right side: Trophy preview */}
          <div className="flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg p-6">
            <div className="text-sm font-medium text-gray-600 mb-2">Trophy Preview</div>
            <div className="text-xs text-gray-500 mb-4">
              (How it will appear on the shelf)
            </div>
            
            {/* Trophy Shelf Preview */}
            <div className="relative w-full flex flex-col items-center">
              {/* Trophy Image */}
              <div className="relative inline-block mb-2">
                <img
                  src={getTrophyImagePath()}
                  alt={`${selectedDesign} trophy`}
                  className="w-36 h-36 object-contain drop-shadow-xl"
                  onError={(e) => {
                    console.error(`Failed to load trophy image: ${getTrophyImagePath()}`);
                    e.target.style.display = 'none';
                    const parent = e.target.parentElement;
                    if (!parent.querySelector('.error-message')) {
                      const errorDiv = document.createElement('div');
                      errorDiv.className = 'error-message text-center text-red-500 text-sm';
                      errorDiv.textContent = `Trophy image not found: ${selectedDesign}_${selectedColor}.png`;
                      parent.appendChild(errorDiv);
                    }
                  }}
                />
              </div>
              
              {/* Shelf Line */}
              <div 
                className="w-48 h-2 rounded-sm mb-2"
                style={{
                  background: '#654321',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              ></div>
              
              {/* Text Under Shelf - Nameplate Text + Caption Area */}
              <div className="flex flex-col items-center" style={{ width: '120px' }}>
                {/* Nameplate Text */}
                {nameplateText && (
                  <div 
                    className="font-bold text-center leading-tight mb-0.5"
                    style={{
                      fontSize: `${fontSize * 0.9}rem`,
                      color: '#000000',
                      wordWrap: 'break-word',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%'
                    }}
                  >
                    {nameplateText}
                  </div>
                )}
                {/* Session Title Placeholder */}
                <div className="text-xs text-gray-500 text-center leading-tight italic">
                  [Session Title]
                </div>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 mt-4 text-center max-w-xs">
              Nameplate text displays above the session title, below the shelf
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrophySelector;
